import { useEffect, useState } from "react";
import DemandForecastFilter from "../../components/Filter/demandForecastFilter";
import { OverviewCard } from "../../components/OverviewCard";
import { FilterConfig, IDemandPayload, IOverview, IRoot } from "../../services/interface/IDemand";
import { IRoot as IForecastRoot} from "../../services/interface/IHome";
import Graph from "../../components/ForecastOverview/graph";
import MapComponent from "../../components/ForecastOverview/map";
import { useForecastOverViewDropDownFilter, useGetFeatchForecastoverView } from "../../services/redux/hooks/useDemand";
import { transformData } from "../../utils/formatter";
import { useDispatch, useSelector } from "react-redux";
import { setApplyFilter, setClearState, setDBFilter, setMapDC, setMapStore, setSelectedDBFilter, setSelectedFlagMarker } from "../../services/redux/reducer/demandfilter.reducer";
import CustomLoader from "../../common/CustomLoader/CustomLoader";

const ForecastOverview = () => {
    const initialState: IDemandPayload = {
        dc: [],
        storeNumber: [],
        itemId: [],
        fcwNumber: ["3"],
        ucode: [],
        productSegm: [],
        productDesc: [],
        category: [],
        subCategory: [],
        vendorName: [],
        modelType: ["error based best model"],
    }
    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const [overViewData, setOverViewData] = useState<IOverview[]>();
    // filter api - data
    const [filter, setFilter] = useState<IDemandPayload>(initialState); 
    // selected filter - data
    const [selectedFilterData, setSelectedFilterData] = useState<IDemandPayload>(initialState);
    // selected filter - redux
    const selectedDbFilter = useSelector((state:IRoot)=>state.dbfilter.selectedDbFilter);
    // selected DC - Map
    const mapdc = useSelector((state:IRoot)=>state.dbfilter.mapdc);
    // selected store - Map
    const mapstore = useSelector((state:IRoot)=>state.dbfilter.mapstore);
    const applyFilter = useSelector((state:IRoot)=>state.dbfilter.applyFilter);
    const selectedFlagMarker = useSelector((state:IRoot)=>state.dbfilter.selectedFlagMarker);
    //last Updated
    const lastUpdated = useSelector((state:IForecastRoot)=>state.filter.lastUpdateDate) || "";
    const { mutate, data } = useGetFeatchForecastoverView();
    const {mutate: mutateDropDown, data: resFilterData} = useForecastOverViewDropDownFilter();
    
    // Filters - Start
    const filters: FilterConfig[] = [
        { title: 'DC', key: 'dc', placeholder: 'All' },
        { title: 'Retail Store', key: 'storeNumber', placeholder: 'All' },
        { title: 'Category', key: 'category', placeholder: 'All' },
        { title: 'SubCategory', key: 'subCategory', placeholder: 'All' },
        { title: 'Segmentation', key: 'productSegm', placeholder: 'All' },
        { title: 'Product Desc.', key: 'productDesc', placeholder: 'All' },
        { title: 'Item Id', key: 'itemId', placeholder: 'All' },
        { title: 'Forecast Week', key: 'fcwNumber', placeholder: '3', isSelectPicker: true },
        { title: 'Vendor', key: 'vendorName', placeholder: 'All' },
    ];    

    const filterHandler=async()=>{
        setLoading(true);
        try{
            await mutateDropDown(selectedFilterData);
        }
        catch(err){
            console.error(err);
        }
        finally{
            setLoading(false);
        }
    }
    
    const setFilterData=()=>{
        if(resFilterData?.data){
            setFilter(resFilterData?.data);
            dispatch(setDBFilter(resFilterData?.data));
        }
    }

    const filterSelectionHandler=(key:any,value:any)=>{
        setSelectedFilterData({
            ...selectedFilterData,
            [key]:value
        });
    }

    const removeSelectionHandler=()=>{
        setSelectedFilterData(initialState);
    }
    // Filters - End

    useEffect(()=>{
        dispatch(setSelectedDBFilter(selectedFilterData));
    },[selectedFilterData]);

    useEffect(()=>{
        setFilterData();
        return ()=>{
            dispatch(setSelectedDBFilter(initialState));
        }
    },[resFilterData?.data]);

    useEffect(() => {
        if (applyFilter === "" || applyFilter === "applied" || ["drilldown", "drillup"].includes(applyFilter)) {
            let mergedFilter: IDemandPayload = {...selectedDbFilter};
            if (applyFilter === "drilldown" && selectedFlagMarker?.location?.length > 0) {

                mergedFilter.dc = selectedDbFilter.dc && selectedDbFilter.dc.length > 0 ? 
                    selectedDbFilter.dc : mapdc;
                mergedFilter.storeNumber = selectedDbFilter.storeNumber && selectedDbFilter.storeNumber.length > 0 ?
                    selectedDbFilter.storeNumber : mapstore;
                mutate(mergedFilter);

            } else if (applyFilter === "drillup") {

                mergedFilter.dc = selectedDbFilter.dc && selectedDbFilter?.dc?.length > 0 ?
                    selectedDbFilter.dc : mapdc;
                mergedFilter.storeNumber = selectedDbFilter.storeNumber && selectedDbFilter.storeNumber.length > 0 ?
                    selectedDbFilter.storeNumber : mapstore;
                mutate(mergedFilter);

            } else if (applyFilter === "" || applyFilter === "applied") {

                if (selectedFlagMarker?.location?.length <= 0) {
                    mergedFilter = {
                        ...selectedDbFilter
                    };
                    mergedFilter.dc = selectedDbFilter.dc && selectedDbFilter.dc.length > 0 ?
                        selectedDbFilter.dc : mapdc;
                    mergedFilter.storeNumber = selectedDbFilter.storeNumber && selectedDbFilter.storeNumber.length > 0 ?
                        selectedDbFilter.storeNumber : mapstore;
                } else {
                    mergedFilter.dc = selectedDbFilter.dc && selectedDbFilter.dc.length > 0 ?
                        selectedDbFilter.dc : [selectedFlagMarker.branchId];
                    mergedFilter.storeNumber = selectedDbFilter.storeNumber && selectedDbFilter.storeNumber.length > 0 ?
                        selectedDbFilter.storeNumber : mapstore;
                }
                mutate(mergedFilter);
            }
        }
    }, [applyFilter, selectedFlagMarker]);

    useEffect(() => {
        try{
            setLoading(true);
            if (data) {
                setOverViewData(transformData(data?.data));
                setLoading(false);
            }
        }
        catch(err){
            console.error(err);
            setLoading(false);
        }
    }, [data]);

    return <>
        <div className="flex flex-col h-[88vh]">
            <div className="relative">
                <DemandForecastFilter
                    selectedFilterData={selectedFilterData}
                    filterSelectionHandler={filterSelectionHandler}
                    removeSelectionHandler={removeSelectionHandler}
                    resFilterData={filter}
                    filters={filters}
                    filterHandler={filterHandler}
                    alertDesc="Demand Forecasting > Forecast Overview"
                    lastUpdated={lastUpdated} />
            </div>

            <div className="bg-[#F1F1F1] h-[73vh] flex flex-col">
                <div className="h-[17vh] grid grid-cols-6 px-[2%] py-[10px] gap-2">
                    {overViewData?.map((data: any, index: number) => {
                        const isAbsolute = ["forecastDemand","Avg of Last 8 Weeks"].includes(data.key) ? true : false;
                        const updatedData = { ...data, isAbsolute };
                        return (
                                <OverviewCard
                                    key={index}
                                    card={updatedData}
                                />
                        );
                    })}
                </div>
                <div className="bg-[#F1F1F1] h-[55vh] px-[2%] py-[0px] pb-[10px] grow">
                    <div className="flex gap-[0.45rem] h-full">
                        <MapComponent title={"Total Forecast (Next 5 Weeks) All Branches"} />
                        <Graph title={"Historical Demand vs Forecast"} />
                    </div>
                </div>
            </div>
            {loading && <CustomLoader open={loading} />}
        </div>
    </>
}

export default ForecastOverview;